import { useEffect, useState } from 'react';
import { BridgeApiConversation, BridgeApiTemplate, InternalApiAlertRfi, InternalApiAlertType } from '../api-internal';
import { alertRfiApi, alertRfiMessageApi, bridgeTemplateApi, conversationApi } from '../apis';

export interface RFIData {
  loading: boolean;
  rfi?: InternalApiAlertRfi;
  rfiTemplate?: BridgeApiTemplate;
  conversation?: BridgeApiConversation;
  messageCount?: number;
  reloadRfiData: () => void;
}

export default function useRfiData(
  alertId: number,
  alertType: InternalApiAlertType,
  rfiSendingEnabled: boolean
): RFIData {
  const [rfiData, setRfiData] = useState<Omit<RFIData, 'reloadRfiData'>>({
    loading: rfiSendingEnabled,
  });
  const reloadRfiData = () => {
    if (!rfiData.loading) setRfiData({ loading: true });
    fetchRfiData(alertId, alertType).then((it) => setRfiData(it));
  };
  useEffect(() => {
    rfiSendingEnabled && reloadRfiData();
  }, [alertId, alertType, rfiSendingEnabled]);
  return {
    ...rfiData,
    reloadRfiData,
  };
}

async function fetchRfiData(alertId: number, alertType: InternalApiAlertType): Promise<Omit<RFIData, 'reloadRfiData'>> {
  const alertRfi = await tryOrNull(() =>
    alertRfiApi.getAlertRfi({
      alertId: alertId,
      alertType,
    })
  );
  if (!alertRfi || !alertRfi.id) {
    return {
      loading: false,
    };
  }
  const conv = await tryOrNull(() =>
    conversationApi.getConversationByHandle({
      id: alertRfi.conversationId,
    })
  );
  const template = await tryOrNull(() =>
    bridgeTemplateApi.findTemplateByHandle({
      id: alertRfi.templateId,
      stateAt: conv?.creationDate,
    })
  );
  const messageNumber = await tryOrNull(() =>
    alertRfiMessageApi.findAllAlertRfiMessages({
      alertRfiId: alertRfi.id,
      page: 0,
      size: 1,
    })
  );
  return {
    loading: false,
    rfi: alertRfi,
    rfiTemplate: template,
    conversation: conv,
    messageCount: messageNumber?.total,
  };
}

async function tryOrNull<Type>(func: () => Promise<Type>): Promise<Type | undefined> {
  try {
    return await func();
  } catch (_) {
    return undefined;
  }
}
